import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Tabs,
  Text,
} from '@mantine/core'
import {
  useMemo,
  useState,
} from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { useQueryClient } from '@tanstack/react-query'

import Device, { DeviceIDTypes } from '@data/device/model/device'
import Account from '@data/accounts/model/account'
import CustomerLocation from '@data/locations/model/customerLocation'

import { useAuthStore } from '@store/auth.store'
import { useAccounts } from '@data/accounts/accounts'
import { useVisibleLocations } from '@data/locations/locations'
import {
  useDeviceDataUpdate,
  useDeviceFirmwareUpdate,
  useDevicePulsesUpdate,
  useDeviceQueryMutationState,
  useDeviceRefresh,
  useDeviceReset,
  useTimezonesQuery,
} from '@data/device/device'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'
import {
  hasValveSettingsCache,
  refreshValveSettings,
} from '@data/device/device'
import { notifyApiError } from '@common/utils/notify'

import { DeviceInfoTab } from './components/DeviceInfoTab'
import { DeviceMonitoringTab } from './components/DeviceMonitoringTab'
import { DeviceNetworkTab } from './components/DeviceNetworkTab'
import { DeviceStatusTab } from './components/DeviceStatusTab'
import { DeviceLocationTab } from './components/DeviceLocationTab'
import { DeviceAccountTab } from './components/DeviceAccountTab'
import { DeviceCommandsTab } from './components/DeviceCommandsTab'

import { FIRMWARE_OPTIONS } from '@components/main/DeviceManagement/DeviceInformationManager/constants'
import { DeviceValvesTab } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/DeviceValvesTab'
import ValveScheduleTab from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveScheduleTab'
import { hasPermissions } from '@common/utils/validation/authValidators'
import {
  Maximize2,
  Minimize2,
} from 'lucide-react'


export interface DeviceModalProps {
  opened: boolean
  device: Device
  onClose: () => void
}

export const DeviceModalMantine = ({
                                     opened,
                                     device,
                                     onClose,
                                   }: DeviceModalProps) => {

  const accounts = useAccounts()
  const locations = useVisibleLocations()

  const {
    deviceUpdating,
    deviceSendingMessage,
    error: deviceError,
  } = useDeviceQueryMutationState()
  const {
    data: timezones = [],
    isPending: timezonesLoading,
    isError: timezonesError,
  } = useTimezonesQuery()

  const permissions = useAuthStore((state) => state.permissions)
  const { data: userInfo } = useCurrentUserQuery()
  const queryClient = useQueryClient()

  const { mutateAsync: updateDeviceData } = useDeviceDataUpdate()
  const { mutateAsync: updateDevicePulses } = useDevicePulsesUpdate()
  const { mutateAsync: loadDevice } = useDeviceRefresh()
  const { mutate: updateFirmware } = useDeviceFirmwareUpdate()
  const { mutate: resetDevice } = useDeviceReset()

  const [activeTab, setActiveTab] = useState<string>('info')
  const [fullScreen, setFullScreen] = useState(false)
  const isMobile = useMediaQuery('(max-width: 47.99em)')
  const isFullScreen = isMobile || fullScreen

  const deviceAccount = useMemo<Account | undefined>(
    () => accounts.find((a) => a.id === device.accountId),
    [accounts, device.accountId],
  )

  const deviceLocation = useMemo<CustomerLocation | undefined>(
    () => locations.find((l) => l.id === device.deviceLocationId),
    [locations, device.deviceLocationId],
  )

  const deviceAdmin = useMemo<boolean>(
    () =>
      hasPermissions(permissions, ['UPDATE:DEVICE:PROD:ADMIN']),
    [permissions],
  )

  const canReadValveSettings = useMemo(
    () => hasPermissions(permissions, ['READ:DEVICE:PROD']),
    [permissions],
  )

  const userUpdateFields =
    (hasPermissions(permissions, ['UPDATE:DEVICE:PROD:USER']) &&
      (userInfo?.adminCustomerLocationIds?.includes(
          device.deviceLocationId,
        ) ||
        userInfo?.adminAccountIds?.includes(device.accountId)))

  // =====================================================
  // Handlers
  // =====================================================

  const handleRefreshData = () => {
    void loadDevice({
      deviceId: device.dUuid,
      idType: DeviceIDTypes.dUuid,
    })

    if (canReadValveSettings && hasValveSettingsCache(queryClient, device.dUuid)) {
      void refreshValveSettings(queryClient, device.dUuid).catch((error) => {
        notifyApiError(error, 'Unable to refresh valve settings.')
      })
    }
  }

  const handleUpdateDeviceInfo = async (updatedDevice: Device) => {
    await updateDeviceData({
      deviceId: device.dUuid,
      idType: DeviceIDTypes.dUuid,
      device: updatedDevice,
    })
  }

  const handleUpdatePulses = async (pulses: number) => {
    await updateDevicePulses({
      deviceId: device.dUuid,
      idType: DeviceIDTypes.dUuid,
      pulsesPerLiter: pulses,
    })
  }

  const handleFirmwareUpdate = (binFileName: string) => {
    updateFirmware({
      deviceId: device.dUuid,
      idType: DeviceIDTypes.dUuid,
      firmwareVersion: binFileName,
    })
  }

  const handleReset = () => {
    resetDevice({
      deviceId: device.dUuid,
      idType: DeviceIDTypes.dUuid,
    })
  }

  if (!device) return null

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      fullScreen={isFullScreen}
      centered={!isFullScreen}
      size="80%"
      radius={isFullScreen ? 0 : 'md'}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Group justify="space-between" style={{ width: '100%' }}>
            <Modal.Title>
              <Text fw={600}>{device.deviceName}</Text>
            </Modal.Title>

            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                c={'dimmed'}
                aria-label={fullScreen ? 'Exit full screen' : 'Open full screen'}
                visibleFrom='sm'
                onClick={() => setFullScreen((v) => !v)}
              >
                {fullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </ActionIcon>
              <Modal.CloseButton onClick={onClose} />
            </Group>
          </Group>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600} size="md">
                {device.deviceId}
              </Text>

              <Button
                variant="light"
                onClick={handleRefreshData}
                loading={deviceUpdating}
              >
                Refresh
              </Button>
            </Group>

            {deviceError && (
              <Text c="red" size="sm">
                {deviceError}
              </Text>
            )}

            <Tabs
              value={activeTab}
              onChange={(value) => setActiveTab(value || 'info')}
            >
              <Tabs.List>
                <Tabs.Tab value="info">
                  Info
                </Tabs.Tab>

                {deviceAdmin && (
                  <Tabs.Tab value="monitoring">
                    Monitoring
                  </Tabs.Tab>
                )}

                {deviceAdmin &&
                  device.deviceVendor === 'SMARTFLOW' && (
                    <Tabs.Tab value="commands">
                      Device
                    </Tabs.Tab>
                  )}

                {device.deviceVendor === 'SMARTFLOW' && (
                  <Tabs.Tab value="valves">
                    Valves
                  </Tabs.Tab>
                )}

                {deviceAdmin &&
                  device.deviceVendor === 'SMARTFLOW' && (
                    <Tabs.Tab value="valve_schedule">
                      Valve Schedule
                    </Tabs.Tab>
                  )}

                <Tabs.Tab value="network">
                  Network
                </Tabs.Tab>

                <Tabs.Tab value="status">
                  Status
                </Tabs.Tab>

                {deviceAdmin && (
                  <Tabs.Tab value="location">
                    Location
                  </Tabs.Tab>
                )}

                {deviceAdmin && (
                  <Tabs.Tab value="account">
                    Account
                  </Tabs.Tab>
                )}

              </Tabs.List>

              <Tabs.Panel value="info" pt="md">
                <DeviceInfoTab
                  device={device}
                  timezones={timezones}
                  timezonesLoading={timezonesLoading}
                  timezonesError={timezonesError}
                  deviceAdmin={deviceAdmin}
                  userUpdateFields={userUpdateFields}
                  deviceUpdating={deviceUpdating}
                  onUpdate={handleUpdateDeviceInfo}
                  onUpdatePulses={handleUpdatePulses}
                />
              </Tabs.Panel>

              {deviceAdmin && (
                <Tabs.Panel value="monitoring" pt="md">
                  <DeviceMonitoringTab
                    device={device}
                    deviceAdmin={deviceAdmin}
                    deviceUpdating={deviceUpdating}
                    onUpdate={handleUpdateDeviceInfo}
                    onUpdatePulses={handleUpdatePulses}
                  />
                </Tabs.Panel>
              )}

              <Tabs.Panel value="network" pt="md">
                <DeviceNetworkTab
                  device={device}
                  showSimDetails={deviceAdmin}
                />
              </Tabs.Panel>

              <Tabs.Panel value="status" pt="md">
                <DeviceStatusTab
                  device={device}
                  permissions={permissions}
                />
              </Tabs.Panel>

              {device.deviceVendor === 'SMARTFLOW' && (
                <Tabs.Panel value="valves" pt="md">
                  <DeviceValvesTab
                    device={device}
                    permissions={permissions}
                    deviceUpdating={deviceUpdating}
                    isActive={opened && activeTab === 'valves'}
                  />
                </Tabs.Panel>
              )}

              {deviceAdmin &&
                device.deviceVendor === 'SMARTFLOW' && (
                  <Tabs.Panel value="valve_schedule" pt="md">
                    <ValveScheduleTab
                      device={device}
                    />
                  </Tabs.Panel>
                )}

              {deviceAdmin && (
                <Tabs.Panel value="location" pt="md">
                  <DeviceLocationTab
                    location={deviceLocation}
                  />
                </Tabs.Panel>
              )}

              {deviceAdmin && (
                <Tabs.Panel value="account" pt="md">
                  <DeviceAccountTab
                    account={deviceAccount}
                  />
                </Tabs.Panel>
              )}

              {deviceAdmin &&
                device.deviceVendor === 'SMARTFLOW' && (
                  <Tabs.Panel value="commands" pt="md">
                    <DeviceCommandsTab
                      device={device}
                      deviceSendingMessage={deviceSendingMessage}
                      firmwareOptions={FIRMWARE_OPTIONS}
                      permissions={permissions}
                      onFirmwareUpdate={handleFirmwareUpdate}
                      onReset={handleReset}
                    />
                  </Tabs.Panel>
                )}
            </Tabs>

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={onClose}
              >
                Close
              </Button>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}
